import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MediaScaleMode, Screen, ScreenRotationDegrees } from '@ghostscreen/contracts';
import { ConfirmDialog } from '../../components/dashboard/ConfirmDialog';
import { OfflineReadinessLine } from '../../components/dashboard/OfflineReadinessLine';
import { apiErrorMessage } from '../../i18n';
import { useDesktopLayout } from '../../layout/useDesktopLayout';
import { api } from '../../lib/api';
import { contentUploadForScreenPath } from '../content/contentRoutes';
import { DisplayContentSyncCard } from './DisplayContentSyncCard';
import { DisplayRotationControl } from './DisplayRotationControl';
import { DisplayScaleModeControl } from './DisplayScaleModeControl';
import { DisplaySetupPreview } from './DisplaySetupPreview';
import { ManageDangerZone, type StatusMessage } from './manage/ManageDangerZone';
import { ManageTabBar } from './manage/ManageTabBar';
import { managePlayingCopy } from './manage/managePlayingCopy';
import { defaultManageTab, type ManageTabId } from './manage/manageTabs';
import { DisplayNowPlaying } from './now-playing/DisplayNowPlaying';
import { DisplayPlaybackProof } from './now-playing/DisplayPlaybackProof';
import { useRemoveDirectItem } from './now-playing/useRemoveDirectItem';
import { FixThisScreenSection } from './fixScreen/FixThisScreenSection';
import { DisplayPairingSection } from './pairing/DisplayPairingSection';
import { PlayContentPicker } from './play-content/PlayContentPicker';
import { usePlayContentFlow } from './play-content/usePlayContentFlow';
import { useDisplayPreviewSlides } from './useDisplayPreviewSlides';
import { useScreenOfflineReadiness } from './useScreenOfflineReadiness';

type PendingAction = 'disconnect' | 'delete';

type ScreenManagePanelProps = {
  organisationId: string;
  screen: Screen;
  playlists: { id: string; name: string }[];
  unpaired: boolean;
  /** Open the play-content picker immediately (board card → play). */
  startPlayContent?: boolean;
  onDeleted?: () => void;
};

export function ScreenManagePanel({
  organisationId,
  screen,
  playlists,
  unpaired: unpairedProp,
  startPlayContent = false,
  onDeleted,
}: ScreenManagePanelProps) {
  const { t } = useTranslation('displays');
  const isDesktop = useDesktopLayout();
  const queryClient = useQueryClient();
  const [disconnectedLocally, setDisconnectedLocally] = useState(false);
  // Local flag + screen fields so disconnect flips to unpaired without a manual refresh.
  const unpaired = disconnectedLocally || screen.deviceId == null || unpairedProp;
  const [tab, setTab] = useState<ManageTabId>(() => defaultManageTab());
  const [pairingExpanded, setPairingExpanded] = useState(unpaired);
  const [draftPlaylistId, setDraftPlaylistId] = useState('');
  const [draftScaleMode, setDraftScaleMode] = useState<MediaScaleMode>(screen.scaleMode);
  const [draftRotationDegrees, setDraftRotationDegrees] = useState<ScreenRotationDegrees>(
    screen.rotationDegrees,
  );
  const [updateStatus, setUpdateStatus] = useState<StatusMessage | null>(null);
  const [dangerStatus, setDangerStatus] = useState<StatusMessage | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [playlistPickerOpen, setPlaylistPickerOpen] = useState(false);
  const { readiness, isPending: readinessPending } = useScreenOfflineReadiness(
    screen,
    organisationId,
  );

  const assignmentQuery = useQuery({
    queryKey: ['screen-assignment', organisationId, screen.id],
    queryFn: () => api.getScreenAssignment(organisationId, screen.id),
  });

  const playContent = usePlayContentFlow({
    organisationId,
    screenId: screen.id,
    startOpen: startPlayContent,
    onPlayed: async (playlistName) => {
      setPlaylistPickerOpen(false);
      setUpdateStatus({
        text: t('manage.status.nowPlaying', { name: playlistName }),
        error: false,
      });
      await assignmentQuery.refetch();
    },
  });

  const assignedPlaylistId = assignmentQuery.data?.playlistId ?? null;

  const publishStateQuery = useQuery({
    queryKey: ['playlist-publish-state', organisationId, assignedPlaylistId],
    queryFn: () => api.getPlaylistPublishState(organisationId, assignedPlaylistId!),
    enabled: Boolean(assignedPlaylistId),
  });
  const hasUnpublishedDraft = publishStateQuery.data?.hasUnpublishedChanges === true;

  useEffect(() => {
    setDraftScaleMode(screen.scaleMode);
    setDraftRotationDegrees(screen.rotationDegrees);
    setUpdateStatus(null);
  }, [screen.id, screen.scaleMode, screen.rotationDegrees]);

  useEffect(() => {
    setDisconnectedLocally(false);
    setTab(defaultManageTab());
    setPairingExpanded(unpairedProp || screen.deviceId == null);
    // Only re-init when switching displays — not on disconnect.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unpaired/device handled below
  }, [screen.id]);

  useEffect(() => {
    if (screen.deviceId != null) setDisconnectedLocally(false);
  }, [screen.deviceId]);

  useEffect(() => {
    if (disconnectedLocally) setPairingExpanded(true);
  }, [disconnectedLocally]);

  useEffect(() => {
    setDraftPlaylistId(assignedPlaylistId ?? '');
  }, [assignedPlaylistId, screen.id]);

  useEffect(() => {
    if (startPlayContent) setTab('content');
  }, [startPlayContent]);

  const { slides: draftSlides, isPending: draftSlidesPending } = useDisplayPreviewSlides(screen, {
    playlistIdOverride: draftPlaylistId || null,
  });

  const scaleDirty = draftScaleMode !== screen.scaleMode;
  const rotationDirty = draftRotationDegrees !== screen.rotationDegrees;
  const playlistDirty = draftPlaylistId !== (assignedPlaylistId ?? '');
  const isDirty = scaleDirty || rotationDirty || playlistDirty;

  const invalidateScreens = () =>
    void queryClient.invalidateQueries({ queryKey: ['screens', organisationId] });

  const clearContent = useMutation({
    mutationFn: () => api.clearScreenAssignment(organisationId, screen.id),
    onSuccess: async () => {
      setDraftPlaylistId('');
      setPlaylistPickerOpen(false);
      setUpdateStatus({ text: t('manage.status.contentRemoved'), error: false });
      invalidateScreens();
      void queryClient.invalidateQueries({
        queryKey: ['screen-assignment', organisationId, screen.id],
      });
      await assignmentQuery.refetch();
    },
    onError: (err) =>
      setUpdateStatus({
        text: apiErrorMessage(err, t('manage.errors.removeContent')),
        error: true,
      }),
  });

  const applySettings = useMutation({
    mutationFn: async () => {
      if (!isDirty) return;
      if (scaleDirty || rotationDirty) {
        const body: {
          scaleMode?: MediaScaleMode;
          rotationDegrees?: ScreenRotationDegrees;
        } = {};
        if (scaleDirty) body.scaleMode = draftScaleMode;
        if (rotationDirty) body.rotationDegrees = draftRotationDegrees;
        await api.updateScreen(organisationId, screen.id, body);
      }
      if (playlistDirty) {
        await api.assignPlaylistToScreen(organisationId, screen.id, draftPlaylistId);
      }
    },
    onSuccess: async () => {
      setUpdateStatus({ text: t('manage.status.settingsSaved'), error: false });
      invalidateScreens();
      await assignmentQuery.refetch();
    },
    onError: (err) =>
      setUpdateStatus({ text: apiErrorMessage(err, t('manage.errors.saveSettings')), error: true }),
  });

  const disconnect = useMutation({
    mutationFn: () => {
      if (!screen.deviceId) throw new Error('No device linked');
      return api.revokeDevice(organisationId, screen.deviceId);
    },
    onSuccess: async () => {
      setPendingAction(null);
      setDisconnectedLocally(true);
      setDangerStatus({ text: t('manage.status.disconnected'), error: false });
      queryClient.setQueryData<Screen[]>(['screens', organisationId], (prev) =>
        (prev ?? []).map((item) =>
          item.id === screen.id
            ? {
                ...item,
                deviceId: null,
                lastHeartbeatAt: null,
                reportedPlaylistVersion: null,
              }
            : item,
        ),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['screens', organisationId] }),
        queryClient.invalidateQueries({ queryKey: ['devices', organisationId] }),
      ]);
    },
    onError: (err) => {
      setPendingAction(null);
      setDangerStatus({ text: apiErrorMessage(err, t('manage.errors.disconnect')), error: true });
    },
  });

  const removeDisplay = useMutation({
    mutationFn: () => api.deleteScreen(organisationId, screen.id),
    onSuccess: () => {
      setPendingAction(null);
      invalidateScreens();
      void queryClient.invalidateQueries({ queryKey: ['devices', organisationId] });
      onDeleted?.();
    },
    onError: (err) => {
      setPendingAction(null);
      setDangerStatus({ text: apiErrorMessage(err, t('manage.errors.delete')), error: true });
    },
  });

  const assignedPlaylistName =
    assignmentQuery.data?.playlistName ??
    playlists.find((p) => p.id === assignedPlaylistId)?.name;
  const assignedIsDirect = assignmentQuery.data?.playlistKind === 'direct';

  const draftPlaylistName = playlists.find((p) => p.id === draftPlaylistId)?.name;

  const selectPlaylistId = assignedIsDirect && !playlistDirty ? '' : draftPlaylistId;

  const removeDirectItem = useRemoveDirectItem({
    organisationId,
    screenId: screen.id,
    playlistId: assignedPlaylistId ?? '',
    onCleared: () => {
      setDraftPlaylistId('');
      setUpdateStatus({ text: t('manage.status.contentRemoved'), error: false });
      void assignmentQuery.refetch();
    },
    onError: (message) => setUpdateStatus({ text: message, error: true }),
  });

  const nowPlayingMode: 'standard' | 'direct' | 'draft-standard' | null = (() => {
    if (!assignedPlaylistId && !draftPlaylistId) return null;
    if (playlistDirty && draftPlaylistId) return 'draft-standard';
    if (assignedIsDirect) return 'direct';
    if (assignedPlaylistId || draftPlaylistId) return 'standard';
    return null;
  })();

  const nowPlayingName =
    (playlistDirty ? draftPlaylistName : null) ??
    assignedPlaylistName ??
    draftPlaylistName ??
    t('manage.playlistFallback');

  const showContent = tab === 'content';
  const showConnect = tab === 'connect';
  const showSettings = tab === 'settings';
  const showPairing = unpaired || pairingExpanded;
  /** Desktop: preview stays on the right for every tab. Mobile: with Content. */
  const showPreviewPane = isDesktop || showContent;
  const showUpdatePane = showContent || showSettings;
  const playingCopy = managePlayingCopy(Boolean(assignedPlaylistId || draftPlaylistId));

  const connectSection = (
    <div className="space-y-5">
      {unpaired ? (
        <div className="rounded-[12px] border border-dashed border-line bg-panel2 px-4 py-3 text-sm text-ink">
          <p className="m-0 font-semibold">{t('manage.connect.notConnectedTitle')}</p>
          <p className="m-0 mt-1 text-muted">{t('manage.connect.notConnectedBody')}</p>
        </div>
      ) : null}

      {showPairing ? (
        <DisplayPairingSection
          key={screen.deviceId ?? 'unpaired'}
          organisationId={organisationId}
          screen={screen}
          unpaired={unpaired}
        />
      ) : (
        <div className="rounded-[12px] border border-line bg-panel2 px-4 py-3">
          <p className="m-0 text-sm font-semibold text-ink">{t('manage.connect.tvConnectedTitle')}</p>
          <p className="m-0 mt-1 text-sm text-muted">{t('manage.connect.tvConnectedBody')}</p>
          <button
            type="button"
            onClick={() => setPairingExpanded(true)}
            className="mt-3 rounded-[10px] border border-line px-3.5 py-2 text-sm font-semibold text-ink hover:border-[var(--gs-accent)]"
          >
            {t('manage.connect.connectDifferent')}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,28rem)] lg:items-start">
      {pendingAction === 'disconnect' ? (
        <ConfirmDialog
          title={t('manage.confirmDisconnect.title')}
          description={t('manage.confirmDisconnect.description', { name: screen.name })}
          confirmLabel={t('manage.confirmDisconnect.confirm')}
          tone="danger"
          confirming={disconnect.isPending}
          onCancel={() => {
            if (!disconnect.isPending) setPendingAction(null);
          }}
          onConfirm={() => {
            setDangerStatus(null);
            disconnect.mutate();
          }}
        />
      ) : null}

      {pendingAction === 'delete' ? (
        <ConfirmDialog
          title={t('manage.confirmDelete.title')}
          description={t('manage.confirmDelete.description', { name: screen.name })}
          confirmLabel={t('manage.confirmDelete.confirm')}
          tone="danger"
          confirming={removeDisplay.isPending}
          onCancel={() => {
            if (!removeDisplay.isPending) setPendingAction(null);
          }}
          onConfirm={() => {
            setDangerStatus(null);
            removeDisplay.mutate();
          }}
        />
      ) : null}

      <div className="min-w-0 space-y-5">
        <ManageTabBar
          value={tab}
          onChange={setTab}
          connectAttention={unpaired}
        />

        {showContent ? (
          <div className="space-y-5" role="tabpanel" aria-label={t('manage.tabs.content')}>
            <section className="space-y-3">
              <h3 className="m-0 text-sm font-bold text-ink">{t('manage.content.heading')}</h3>
              {assignedPlaylistName ? (
                <p className="m-0 text-sm text-muted">
                  <Trans
                    t={t}
                    i18nKey="manage.content.liveNow"
                    values={{ name: assignedPlaylistName }}
                    components={{ strong: <span className="font-semibold text-ink" /> }}
                  />
                  {assignedIsDirect ? (
                    <span className="text-muted"> · {t('manage.content.thisDisplayOnly')}</span>
                  ) : null}
                  {playlistDirty && draftPlaylistName ? (
                    <>
                      {' · '}
                      <Trans
                        t={t}
                        i18nKey="manage.content.previewing"
                        values={{ name: draftPlaylistName }}
                        components={{ strong: <span className="font-semibold text-ink" /> }}
                      />
                    </>
                  ) : null}
                </p>
              ) : (
                <p className="m-0 text-sm text-muted">{playingCopy.emptyHint}</p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPlaylistPickerOpen(false);
                    playContent.openPicker();
                  }}
                  className="min-w-0 flex-1 rounded-[10px] px-3 py-2.5 text-sm font-bold text-[var(--gs-accent-ink)]"
                  style={{ background: 'var(--gs-accent-solid)' }}
                  aria-expanded={playContent.open}
                >
                  {playingCopy.addLabel}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    playContent.closePicker();
                    setPlaylistPickerOpen((open) => !open);
                  }}
                  className="min-w-0 flex-1 rounded-[10px] border border-line px-3 py-2.5 text-sm font-semibold text-ink hover:border-[var(--gs-accent)]"
                  aria-expanded={playlistPickerOpen}
                >
                  {playingCopy.playlistLabel}
                </button>
              </div>

              {playlistPickerOpen && !playContent.open ? (
                <div className="space-y-3 rounded-[10px] border border-line bg-panel2 px-3 py-3">
                  <p className="m-0 text-sm text-muted">{t('manage.content.sharedShowsHint')}</p>
                  <label className="block min-w-0 text-xs font-semibold text-muted">
                    {t('manage.content.playlist')}
                    <select
                      value={selectPlaylistId}
                      onChange={(e) => {
                        setUpdateStatus(null);
                        setDraftPlaylistId(e.target.value);
                      }}
                      className="mt-1 w-full rounded-[10px] border border-line bg-field px-3 py-2.5 text-sm font-medium text-ink"
                    >
                      <option value="">{t('manage.content.selectPlaylist')}</option>
                      {playlists.map((playlist) => (
                        <option key={playlist.id} value={playlist.id}>
                          {playlist.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  {playlists.length === 0 ? (
                    <p className="m-0 text-sm text-muted">
                      <Trans
                        t={t}
                        i18nKey="manage.content.noPlaylists"
                        components={{
                          playlistsLink: (
                            <Link
                              to="/playlists"
                              className="font-semibold text-accent hover:text-signal"
                            />
                          ),
                        }}
                      />
                    </p>
                  ) : null}
                </div>
              ) : null}

              {nowPlayingMode ? (
                <DisplayNowPlaying
                  mode={nowPlayingMode}
                  playlistName={nowPlayingName}
                  slides={draftSlides}
                  busy={
                    playContent.playPending ||
                    clearContent.isPending ||
                    removeDirectItem.isPending ||
                    draftSlidesPending
                  }
                  removingItemId={
                    removeDirectItem.isPending
                      ? ((removeDirectItem.variables as string | undefined) ?? null)
                      : null
                  }
                  onAdd={() => {
                    setPlaylistPickerOpen(false);
                    playContent.openPicker();
                  }}
                  onRemoveAssignment={() => {
                    setUpdateStatus(null);
                    if (assignedPlaylistId && !playlistDirty) {
                      clearContent.mutate();
                      return;
                    }
                    setDraftPlaylistId(assignedPlaylistId ?? '');
                    setPlaylistPickerOpen(false);
                  }}
                  onRemoveDirectItem={(itemId) => {
                    setUpdateStatus(null);
                    removeDirectItem.mutate(itemId);
                  }}
                />
              ) : null}

              {!unpaired ? (
                <DisplayPlaybackProof lastPlayed={screen.lastPlayed} lastFailure={screen.lastFailure} />
              ) : null}

              {playContent.open ? (
                <PlayContentPicker
                  screenName={screen.name}
                  assets={playContent.readyAssets}
                  mediaPending={playContent.mediaPending}
                  mediaError={playContent.mediaError}
                  playingAssetId={playContent.playingAssetId}
                  playPending={playContent.playPending}
                  error={playContent.error}
                  columns={3}
                  uploadPath={contentUploadForScreenPath(screen.id)}
                  onSelect={playContent.selectAsset}
                  onCancel={playContent.closePicker}
                />
              ) : null}

              {!unpaired && readiness ? (
                <OfflineReadinessLine readiness={readiness} density="panel" />
              ) : null}
              {!unpaired && !readiness && !readinessPending && assignedPlaylistId ? (
                <p className="m-0 text-sm text-muted">{t('manage.content.publishForReadiness')}</p>
              ) : null}
            </section>

            <DisplayContentSyncCard
              organisationId={organisationId}
              screen={screen}
              unpaired={unpaired}
              assignedPlaylistId={assignedPlaylistId}
              hasUnpublishedDraft={hasUnpublishedDraft}
            />
          </div>
        ) : null}

        {showConnect ? (
          <div role="tabpanel" aria-label={t('manage.tabs.connect')}>
            {connectSection}
          </div>
        ) : null}

        {showSettings ? (
          <div className="space-y-5" role="tabpanel" aria-label={t('manage.tabs.settings')}>
            {!unpaired ? (
              <FixThisScreenSection organisationId={organisationId} screen={screen} />
            ) : null}

            <section className="space-y-3">
              <h3 className="m-0 text-sm font-bold text-ink">{t('manage.lookOnScreen')}</h3>
              <DisplayScaleModeControl
                value={draftScaleMode}
                disabled={applySettings.isPending}
                onChange={(mode) => {
                  setUpdateStatus(null);
                  setDraftScaleMode(mode);
                }}
              />
              <DisplayRotationControl
                value={draftRotationDegrees}
                disabled={applySettings.isPending}
                onChange={(degrees) => {
                  setUpdateStatus(null);
                  setDraftRotationDegrees(degrees);
                }}
              />
            </section>

            <ManageDangerZone
              unpaired={unpaired}
              hasDevice={Boolean(screen.deviceId)}
              disconnectPending={disconnect.isPending}
              deletePending={removeDisplay.isPending}
              dangerStatus={dangerStatus}
              onDisconnect={() => setPendingAction('disconnect')}
              onDelete={() => setPendingAction('delete')}
            />
          </div>
        ) : null}
      </div>

      {showPreviewPane || showUpdatePane ? (
        <aside className="min-w-0 space-y-4 lg:sticky lg:top-4">
          {showPreviewPane ? (
            <DisplaySetupPreview
              slides={draftSlides}
              scaleMode={draftScaleMode}
              rotationDegrees={draftRotationDegrees}
              screenName={screen.name}
              isPending={draftSlidesPending}
            />
          ) : null}

          {showUpdatePane ? (
            <>
              <button
                type="button"
                disabled={!isDirty || applySettings.isPending || (!draftPlaylistId && playlistDirty)}
                onClick={() => {
                  if (!draftPlaylistId && playlistDirty) {
                    setUpdateStatus({ text: t('manage.content.choosePlaylist'), error: true });
                    return;
                  }
                  setUpdateStatus(null);
                  applySettings.mutate();
                }}
                className="w-full rounded-[10px] px-4 py-3 text-sm font-bold text-[var(--gs-accent-ink)] disabled:opacity-50"
                style={{ background: 'var(--gs-accent-solid)' }}
              >
                {applySettings.isPending ? t('manage.update.updating') : t('manage.update.button')}
              </button>
              {updateStatus ? (
                <p
                  className="m-0 text-sm font-medium"
                  style={{ color: updateStatus.error ? 'var(--gs-bad)' : 'var(--gs-ok)' }}
                  role="status"
                >
                  {updateStatus.text}
                </p>
              ) : isDirty ? (
                <p className="m-0 text-xs text-muted">{t('manage.update.dirtyHint')}</p>
              ) : (
                <p className="m-0 text-xs text-muted">{t('manage.update.idleHint')}</p>
              )}
            </>
          ) : null}
        </aside>
      ) : null}
    </div>
  );
}
